import React, { useState, useEffect } from 'react';
import { Table, Card, Button, message, Tag, Space, Alert } from 'antd';
import { PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { treatmentService } from '../../services';
import dayjs from 'dayjs';

const Treatments = () => {
    const { t } = useTranslation(['treatment', 'common']);
    const navigate = useNavigate();
    const [treatments, setTreatments] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchTreatments();
    }, []);

    const fetchTreatments = async () => {
        setLoading(true);
        try {
            const response = await treatmentService.getAll({});
            if (response.success) {
                setTreatments(response.data);
            }
        } catch (error) {
            message.error(t('treatment:messages.fetchFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleAddTreatment = () => {
        message.info({
            content: 'Please go to a patient\'s page to add treatments',
            duration: 3
        });
        navigate('/patients');
    };

    const columns = [
        {
            title: t('treatment:treatmentNumber'),
            dataIndex: 'treatmentNumber',
            key: 'treatmentNumber'
        },
        {
            title: t('treatment:patient'),
            dataIndex: ['patientId', 'name'],
            key: 'patient',
            render: (name, record) => (
                <Button
                    type="link"
                    onClick={() => navigate(`/patients/${record.patientId?._id}`)}
                >
                    {name}
                </Button>
            )
        },
        {
            title: t('treatment:dentist'),
            dataIndex: ['dentistId', 'name'],
            key: 'dentist'
        },
        {
            title: t('treatment:type'),
            dataIndex: 'treatmentType',
            key: 'treatmentType',
            render: (type) => <Tag color="blue">{type}</Tag>
        },
        {
            title: t('treatment:date'),
            dataIndex: 'date',
            key: 'date',
            render: (date) => dayjs(date).format('MMM DD, YYYY')
        },
        {
            title: t('treatment:cost'),
            dataIndex: 'finalCost',
            key: 'finalCost',
            render: (cost) => `$${cost?.toFixed(2) || 0}`
        },
        {
            title: t('treatment:status'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const colors = {
                    Planned: 'blue',
                    'In Progress': 'orange',
                    Completed: 'green',
                    Cancelled: 'red'
                };
                return <Tag color={colors[status]}>{status}</Tag>;
            }
        },
        {
            title: t('common:actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        onClick={() => navigate(`/patients/${record.patientId?._id}`)}
                    >
                        {t('common:view')}
                    </Button>
                </Space>
            )
        }
    ];

    return (
        <Card>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <h1>{t('treatment:title')}</h1>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTreatment}>
                    {t('treatment:add')}
                </Button>
            </div>

            <Alert
                message="How to add treatments"
                description="To add a new treatment, go to the Patients page, select a patient, and add the treatment from their detail page."
                type="info"
                icon={<InfoCircleOutlined />}
                showIcon
                closable
                style={{ marginBottom: 16 }}
            />

            <Table
                columns={columns}
                dataSource={treatments}
                rowKey="_id"
                loading={loading}
                scroll={{ x: 'max-content' }}
            />
        </Card>
    );
};

export default Treatments;
